import React, { type FC, type HTMLAttributes, type PropsWithChildren, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../Inputs';
import { useTranslation } from "react-i18next";
import { type CssLength } from '../interfaces';

import './ExpandableContent.scss';

/**
 * Text-only mode for `ExpandableContent`.
 *
 * Use this when the content should be truncated by character count instead of
 * by rendered element height. When `maxChars` is provided, the component
 * extracts the text content from `children`, shows a shortened preview when the
 * text exceeds that limit, and toggles to the full text when expanded.
 */
interface ExpandableTextProps extends PropsWithChildren {
    /**
     * Not supported in text truncation mode.
     * Provide `maxChars` to enable character-based expansion behavior instead.
     */
    maxHeight?: never;
    /**
     * Maximum number of characters to show before truncating the text.
     *
     * If the extracted text content is longer than this value, the component
     * displays a `View More` button. Clicking the button reveals the full text
     * and changes the button label to `View Less`.
     */
    maxChars?: number;
}

/**
 * Element-height mode for `ExpandableContent`.
 *
 * Use this when rendering normal React content such as markup, lists, or other
 * block elements that should collapse based on their rendered height.
 */
interface ExpandableElementProps extends PropsWithChildren {
    /**
     * The maximum collapsed height applied to the content container.
     *
     * If the rendered content is taller than this value, the component shows a
     * `View More` button. Defaults to `300px`.
     */
    maxHeight?: CssLength;
    /**
     * Not supported in height-based mode.
     * Provide `maxHeight` or rely on the default `300px` collapsed height.
     */
    maxChars?: never;
}

type ExpandableContentProps = ExpandableTextProps | ExpandableElementProps;

const parseHeight = (height: string) => Number.parseInt(height, 10);

const getTextContent = (content: ReactNode): string => {
    if (typeof content === 'string' || typeof content === 'number') {
        return String(content);
    }

    if (Array.isArray(content)) {
        return content.map(getTextContent).join('');
    }

    if (React.isValidElement<HTMLAttributes<HTMLElement> & { children?: ReactNode }>(content)) {
        if (typeof content.props.dangerouslySetInnerHTML?.__html === 'string') {
            const parser = document.createElement('div');
            parser.innerHTML = content.props.dangerouslySetInnerHTML.__html;

            return parser.textContent ?? '';
        }

        return getTextContent(content.props.children);
    }

    return '';
};

/**
 * Displays content inside a collapsible container with a `View More` /
 * `View Less` toggle when the content exceeds the configured preview size.
 *
 * ## Usage
 * - Pass regular React elements to collapse content by rendered height.
 * - Pass `maxChars` to collapse text content by character count instead.
 *
 * ## Behavior
 * - In height mode, the component compares the rendered content height to
 *   `maxHeight` and only shows the toggle when the content overflows.
 * - In text mode, the component extracts text from `children` and only shows
 *   the toggle when the extracted text exceeds `maxChars`.
 * - When expanded, the button label changes from `View More` to `View Less`.
 */
export const ExpandableContent: FC<ExpandableContentProps> = ({
    children,
    maxHeight = '300px',
    maxChars
}) => {
    const [expanded, setExpanded] = useState(false);
    const [showToggle, setShowToggle] = useState(false);
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const textContent = useMemo(() => getTextContent(children), [children]);
    const { t } = useTranslation('common', { useSuspense: false });

    const collapsedHeight = useMemo(() => parseHeight(maxHeight), [maxHeight]);
    const computedExpandedHeight = useMemo(() => {
        if (contentHeight === null) {
            return `${collapsedHeight + 32}px`;
        }

        return `${contentHeight + 32}px`;
    }, [collapsedHeight, contentHeight]);

    const truncatedText = useMemo(() => {
        if (!maxChars || maxChars <= 3 || textContent.length <= maxChars) {
            return textContent;
        }

        return `${textContent.slice(0, maxChars - 3)}...`;
    }, [maxChars, textContent]);

    const shouldUseCharLimit = typeof maxChars === 'number';

    useEffect(() => {
        if (shouldUseCharLimit) {
            setShowToggle(textContent.length > maxChars);
            return;
        }

        const updateOverflowState = () => {
            if (!contentRef.current) {
                return;
            }

            const { scrollHeight, clientHeight } = contentRef.current;
            const visibleHeight = clientHeight || collapsedHeight;

            setContentHeight(scrollHeight);
            setShowToggle(scrollHeight > visibleHeight);
        };

        updateOverflowState();
        window.addEventListener('resize', updateOverflowState);

        return () => {
            window.removeEventListener('resize', updateOverflowState);
        };
    }, [children, collapsedHeight, maxChars, shouldUseCharLimit, t, textContent]);

    return (
        <div className="expandable_content">
            <div
                ref={contentRef}
                className="expandable_content_container"
                style={shouldUseCharLimit ? undefined : { maxHeight: expanded ? computedExpandedHeight : maxHeight }}
            >
                {shouldUseCharLimit ? (expanded ? textContent : truncatedText) : children}
            </div>
            {showToggle && (
                <Button
                    variant="text"
                    className="expandable_content_button"
                    onClick={() => setExpanded((currentValue) => !currentValue)}
                >
                    {expanded ? t('view_less') : t('view_more')}
                </Button>
            )}
        </div>
    );
};